import { useState } from "react";
import { Button, ButtonGroup, Col, Form, Row, Stack } from "react-bootstrap";
import { CheckLg, PencilFill, TrashFill, XLg } from "react-bootstrap-icons";
import ConfirmPrompt from "../ConfirmPrompt";
import HybridHitlistPicker from "../Pickers/HybridHitlistPicker";
import StationPicker from "../Pickers/StationPicker";
import DigitalHitlistPicker from "../Pickers/DigitalHitlistPicker";
import { PREDICTOR_TARGET_TYPES, PREDICTOR_WEIGHT_PRESETS } from "../constants";

const getTargetLabel = (targetId, options) => options.find(option => option.id === Number(targetId))?.label ?? "Unknown";
const getWeightPreset = weight => Object.entries(PREDICTOR_WEIGHT_PRESETS).find(([, preset]) => Number(weight) === preset.value)?.[0] ?? "custom";
const getWeightLabel = weight => {
    const preset = PREDICTOR_WEIGHT_PRESETS[getWeightPreset(weight)];
    return preset ? `${preset.label} (${preset.value})` : `Custom (${weight})`;
};
const getWeightColor = weight => PREDICTOR_WEIGHT_PRESETS[getWeightPreset(weight)]?.color ?? "#396a93";

export default function PredictorWeightRow({
    item,
    targetOptions,
    dataHandlers,
    invalid,
    minWidth = 600,
    existingStations,
    existingHybridHitlists,
    existingDigitalHitlists,
}) {
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const editing = item.status === "EDIT" || item.status === "ADD";
    const options = targetOptions[item.target_type] ?? [];
    const [weightPreset, setWeightPreset] = useState(getWeightPreset(item.weight));

    const handleTargetTypeChange = value => {
        dataHandlers.change(item, "target_type", value);
        dataHandlers.change(item, "target_id", "");
    };

    const handleWeightPresetChange = value => {
        setWeightPreset(value);
        if (value === "custom") return;
        dataHandlers.change(item, "weight", PREDICTOR_WEIGHT_PRESETS[value].value);
    };

    return (
        <Row className="py-1 border-bottom hoverable-no-pointer align-items-center" style={{ minWidth }}>
            <ConfirmPrompt
                bodyText="Are you sure you want to delete this Predictor weight?"
                cancelText="No, go back"
                handleConfirm={async () => await dataHandlers.remove(item)}
                handleClose={() => setShowDeleteConfirm(false)}
                show={showDeleteConfirm}
            />
            <Col
                xs="auto"
                className="p-0 rounded-1 align-self-stretch border-start"
                style={{
                    "--bs-border-color": PREDICTOR_TARGET_TYPES[item.target_type]?.color,
                    "--bs-border-width": "10px",
                }}></Col>
            <Col xs={3}>
                {editing ? (
                    <Form.Select value={item.target_type} size="sm" onChange={e => handleTargetTypeChange(e.target.value)}>
                        {Object.entries(PREDICTOR_TARGET_TYPES).map(([name, { label }]) => (
                            <option key={name} value={name}>
                                {label}
                            </option>
                        ))}
                    </Form.Select>
                ) : (
                    <Form.Control value={PREDICTOR_TARGET_TYPES[item.target_type]?.label} plaintext readOnly size="sm" />
                )}
            </Col>
            <Col>
                {editing ? (
                    <>
                        {item.target_type === "radio" && (
                            <StationPicker
                                name={`weight-${item.setting_id}`}
                                selectedId={item.target_id}
                                includeCompetitors
                                onChange={value => dataHandlers.change(item, "target_id", value)}
                                onlyInput
                                filterFunction={x => !existingStations.includes(x.radio_id) || x.radio_id === item.target_id}
                            />
                        )}
                        {item.target_type === "hybrid_hitlist" && (
                            <HybridHitlistPicker
                                name={`weight-${item.setting_id}`}
                                selectedId={item.target_id}
                                onChange={value => dataHandlers.change(item, "target_id", value)}
                                onlyInput
                                filterFunction={x => !existingHybridHitlists.includes(x.hybrid_hitlist_id) || x.hybrid_hitlist_id === item.target_id}
                            />
                        )}
                        {item.target_type === "digital_hitlist" && (
                            <DigitalHitlistPicker
                                name={`weight-${item.setting_id}`}
                                selectedId={item.target_id}
                                onChange={value => dataHandlers.change(item, "target_id", value)}
                                onlyInput
                                filterFunction={x => !existingDigitalHitlists.includes(x.digital_hitlist_id) || x.digital_hitlist_id === item.target_id}
                            />
                        )}
                    </>
                ) : (
                    <Form.Control value={getTargetLabel(item.target_id, options)} plaintext readOnly size="sm" />
                )}
            </Col>
            <Col xs={2}>
                {editing ? (
                    <Stack direction="horizontal" gap={2}>
                        <Form.Select value={weightPreset} size="sm" onChange={e => handleWeightPresetChange(e.target.value)}>
                            {Object.entries(PREDICTOR_WEIGHT_PRESETS).map(([value, preset]) => (
                                <option key={value} value={value}>
                                    {preset.label}
                                </option>
                            ))}
                            <option value="custom">Custom</option>
                        </Form.Select>
                        {weightPreset === "custom" && (
                            <Form.Control
                                type="number"
                                min={0}
                                step="0.1"
                                value={item.weight ?? ""}
                                size="sm"
                                className="text-center"
                                style={{ width: 80 }}
                                onChange={e => dataHandlers.change(item, "weight", e.target.value)}
                            />
                        )}
                    </Stack>
                ) : (
                    <Stack direction="horizontal" gap={2}>
                        <div className="rounded-1 flex-shrink-0" style={{ width: 16, height: 16, backgroundColor: getWeightColor(item.weight) }} />
                        <Form.Control value={getWeightLabel(item.weight)} plaintext readOnly size="sm" />
                    </Stack>
                )}
            </Col>
            <Col xs="auto" className="d-flex justify-content-center" style={{ width: 108 }}>
                <ButtonGroup size="sm">
                    {!editing ? (
                        <>
                            <Button variant="ra-yellow" title="Edit" onClick={() => dataHandlers.toggleEdit(item)}>
                                <PencilFill size={20} />
                            </Button>
                            <Button variant="danger" title="Delete" onClick={() => setShowDeleteConfirm(true)}>
                                <TrashFill size={20} />
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="success" title="Save" onClick={async () => await dataHandlers.save(item)} disabled={invalid}>
                                <CheckLg size={20} />
                            </Button>
                            <Button variant="danger" title="Discard" onClick={() => dataHandlers.discard(item)}>
                                <XLg size={20} />
                            </Button>
                        </>
                    )}
                </ButtonGroup>
            </Col>
        </Row>
    );
}
